import React, { useState, useEffect } from 'react';

const API = 'https://familybandhan-backend-933326390640.asia-south1.run.app';

const GEN_LABELS = {
  '-3': 'Gen -3', '-2': 'Grandparents', '-1': 'Parents',
  '0': 'Your generation', '1': 'Children', '2': 'Grandchildren', '3': 'Gen +3',
};

export default function FamilyGallery({ familyEmail, onViewProfile }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null); // { member, photoIndex }

  useEffect(() => {
    if (!familyEmail) return;
    setLoading(true);
    fetch(`${API}/api/members/${encodeURIComponent(familyEmail)}`)
      .then(r => r.json())
      .then(data => {
        const withPhotos = (data.members || []).filter(m =>
          (m.storyPhotoUrls && m.storyPhotoUrls.length > 0) || m.story
        );
        setMembers(withPhotos);
      })
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, [familyEmail]);

  const filtered = filter === 'all'
    ? members
    : members.filter(m => m.branch === filter);

  const totalPhotos = members.reduce((sum, m) => sum + (m.storyPhotoUrls?.length || 0), 0);

  const avatarBg = (m) => {
    if (m.isDeceased) return '#E5E7EB';
    if (m.isCreator) return '#FEF9C3';
    return m.gender === 'male' ? '#DBEAFE' : '#FCE7F3';
  };
  const avatarText = (m) => {
    if (m.isDeceased) return '#6B7280';
    if (m.isCreator) return '#854D0E';
    return m.gender === 'male' ? '#1D4ED8' : '#BE185D';
  };
  const initials = (name) => name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  if (loading) {
    return (
      <div style={s.loadingWrap}>
        <div style={s.loadingText}>Loading family memories…</div>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div style={s.emptyWrap}>
        <div style={s.emptyIcon}>📷</div>
        <div style={s.emptyTitle}>No memories yet</div>
        <div style={s.emptyDesc}>
          Open any family member's profile and add story photos or a biography.
          They'll appear here.
        </div>
      </div>
    );
  }

  return (
    <div style={s.wrap}>
      {/* Meta bar */}
      <div style={s.metaBar}>
        <div style={s.metaCount}>
          <strong>{totalPhotos} photo{totalPhotos !== 1 ? 's' : ''}</strong>
          {' '}across{' '}
          <strong>{members.length} member{members.length !== 1 ? 's' : ''}</strong>
        </div>
        <div style={s.filterRow}>
          {['all', 'paternal', 'maternal'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{ ...s.filterPill, ...(filter === f ? s.filterPillActive : {}) }}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div style={s.grid}>
        {filtered.map(member => {
          const photos = member.storyPhotoUrls || [];
          const hasPhotos = photos.length > 0;
          const bg = avatarBg(member);
          const tc = avatarText(member);
          return (
            <div
              key={member.memberId}
              style={{
                ...s.card,
                border: member.isCreator ? '1.5px solid #D4A017' : '1px solid #E8E0D0',
              }}
              onClick={() => setLightbox({ member, photoIndex: 0 })}
            >
              {/* Photo / Avatar */}
              <div style={{ ...s.cardImg, background: bg }}>
                {hasPhotos ? (
                  <img
                    src={photos[0]}
                    alt={member.name}
                    style={s.cardPhoto}
                  />
                ) : (
                  <span style={{ ...s.cardInitials, color: tc }}>
                    {initials(member.name)}
                  </span>
                )}
                {photos.length > 1 && (
                  <span style={s.photoBadge}>{photos.length} photos</span>
                )}
                {member.isDeceased && (
                  <span style={s.deceasedBadge}>🪔</span>
                )}
              </div>

              {/* Meta */}
              <div style={s.cardMeta}>
                <div style={s.cardName}>
                  {member.name}{member.isCreator ? ' ★' : ''}
                </div>
                <div style={s.cardRelation}>
                  <span style={{
                    display: 'inline-block',
                    width: 7, height: 7, borderRadius: '50%',
                    background: member.isDeceased ? '#9CA3AF' : member.gender === 'male' ? '#93C5FD' : '#F9A8D4',
                    marginRight: 4, verticalAlign: 'middle',
                  }} />
                  {member.relation || '—'} · {GEN_LABELS[String(member.generation)] || `Gen ${member.generation}`}
                  {filter === 'all' && (
                    <span style={s.branchTag}>
                      {member.branch === 'maternal' ? ' · M' : ''}
                    </span>
                  )}
                </div>
                {member.story && (
                  <div style={s.storySnippet}>
                    "{member.story.slice(0, 55)}{member.story.length > 55 ? '…' : ''}"
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div style={s.emptyWrap}>
          <div style={s.emptyDesc}>No members with stories or photos on the {filter} side yet.</div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div style={s.lbOverlay} onClick={() => setLightbox(null)}>
          <div style={s.lbCard} onClick={e => e.stopPropagation()}>
            <button style={s.lbClose} onClick={() => setLightbox(null)}>×</button>

            {/* Photo or avatar */}
            {(lightbox.member.storyPhotoUrls || []).length > 0 ? (
              <img
                src={lightbox.member.storyPhotoUrls[lightbox.photoIndex]}
                alt={lightbox.member.name}
                style={s.lbPhoto}
              />
            ) : (
              <div style={{ ...s.lbPhotoPlaceholder, background: avatarBg(lightbox.member) }}>
                <span style={{ fontSize: 48, color: avatarText(lightbox.member) }}>
                  {initials(lightbox.member.name)}
                </span>
              </div>
            )}

            {/* Nav dots — only if multiple photos */}
            {(lightbox.member.storyPhotoUrls || []).length > 1 && (
              <div style={s.navDots}>
                {lightbox.member.storyPhotoUrls.map((_, i) => (
                  <div
                    key={i}
                    onClick={() => setLightbox({ ...lightbox, photoIndex: i })}
                    style={{
                      ...s.navDot,
                      background: i === lightbox.photoIndex ? '#D4A017' : 'rgba(255,255,255,0.35)',
                    }}
                  />
                ))}
              </div>
            )}

            {/* Info */}
            <div style={s.lbInfo}>
              <div style={s.lbName}>{lightbox.member.name}{lightbox.member.isCreator ? ' ★' : ''}</div>
              <div style={s.lbRelation}>
                {lightbox.member.relation} · {GEN_LABELS[String(lightbox.member.generation)] || `Gen ${lightbox.member.generation}`}
                {lightbox.member.isDeceased ? ' · 🪔 Deceased' : ''}
              </div>
              {lightbox.member.story && (
                <div style={s.lbStory}>
                  "{lightbox.member.story.slice(0, 160)}{lightbox.member.story.length > 160 ? '…' : ''}"
                </div>
              )}
              <button
                style={s.viewProfileBtn}
                onClick={() => {
                  setLightbox(null);
                  if (onViewProfile) onViewProfile(lightbox.member);
                }}
              >
                View full profile →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap: { padding: '16px 20px', background: '#F7F4EF', minHeight: '100%' },
  loadingWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#9CA3AF', fontSize: 14 },
  loadingText: {},
  emptyWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', gap: 10, textAlign: 'center' },
  emptyIcon: { fontSize: 40 },
  emptyTitle: { fontSize: 18, fontWeight: 600, color: '#374151', fontFamily: "'Cormorant Garamond', serif" },
  emptyDesc: { fontSize: 13, color: '#9CA3AF', lineHeight: 1.6, maxWidth: 280 },
  metaBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 },
  metaCount: { fontSize: 13, color: '#6B7280' },
  filterRow: { display: 'flex', gap: 6 },
  filterPill: { padding: '4px 12px', borderRadius: 20, border: '1px solid #ddd', fontSize: 12, color: '#666', background: '#fff', cursor: 'pointer' },
  filterPillActive: { background: '#0e0018', color: '#fff', border: '1px solid #0e0018' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 },
  card: { background: '#fff', borderRadius: 8, overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.15s' },
  cardImg: { width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' },
  cardPhoto: { width: '100%', height: '100%', objectFit: 'cover' },
  cardInitials: { fontSize: 28, fontWeight: 700 },
  photoBadge: { position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 10, padding: '2px 7px', borderRadius: 10 },
  deceasedBadge: { position: 'absolute', top: 6, left: 6, fontSize: 14 },
  cardMeta: { padding: '8px 10px' },
  cardName: { fontSize: 12, fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardRelation: { fontSize: 10, color: '#6B7280', marginTop: 2 },
  branchTag: { color: '#9CA3AF' },
  storySnippet: { fontSize: 10, color: '#9CA3AF', fontStyle: 'italic', marginTop: 4, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' },
  // Lightbox
  lbOverlay: { position: 'fixed', inset: 0, background: 'rgba(10,10,20,0.88)', zIndex: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 },
  lbCard: { background: '#fff', borderRadius: 12, overflow: 'hidden', width: 300, maxWidth: '90vw', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' },
  lbClose: { position: 'absolute', top: 10, right: 12, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.45)', color: '#fff', border: 'none', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1, lineHeight: 1 },
  lbPhoto: { width: '100%', height: 220, objectFit: 'cover', display: 'block' },
  lbPhotoPlaceholder: { width: '100%', height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  navDots: { display: 'flex', justifyContent: 'center', gap: 6, padding: '8px 0 0' },
  navDot: { width: 8, height: 8, borderRadius: '50%', cursor: 'pointer' },
  lbInfo: { padding: '14px 16px' },
  lbName: { fontSize: 15, fontWeight: 600, color: '#111', fontFamily: "'Cormorant Garamond', serif" },
  lbRelation: { fontSize: 12, color: '#888', marginTop: 2, marginBottom: 10 },
  lbStory: { fontSize: 12, color: '#555', fontStyle: 'italic', lineHeight: 1.5, marginBottom: 14 },
  viewProfileBtn: { width: '100%', padding: 9, background: '#0e0018', color: '#D4A017', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};